// Shared rules for postcode areas that may only be insured by specific members.
// Used by both the Vueform validation rule (blocks "Next") and the postcode
// lookup search guard (blocks the lookup itself). Kept in one place so the
// prefixes, allowed members and message never drift apart.

// GIR is listed first so the longest match is obvious; order is irrelevant to
// the startsWith check below. All of these are unique UK postcode areas, so a
// prefix match cannot collide with a mainland postcode.
export const RESTRICTED_POSTCODE_PREFIXES = ['GIR', 'IM', 'GY', 'JE', 'BX'] as const

// Member numbers permitted to insure properties in the restricted areas.
//   888800017 - ST&R Limited
//   997728844 - JMR Limited
export const POSTCODE_ALLOWED_MEMBERS = ['888800017', '997728844'] as const

export const RESTRICTED_POSTCODE_MESSAGE = "We are unable to provide a quote for this postcode"

// True when the postcode falls in one of the restricted areas.
export function isRestrictedPostcode(postcode: string | null | undefined): boolean
{
    if (postcode == null) return false
    const normalized = String(postcode).toUpperCase().replace(/\s+/g, '')
    if (!normalized) return false
    return RESTRICTED_POSTCODE_PREFIXES.some((prefix) => normalized.startsWith(prefix))
}

// True when the member number is allowed to insure the restricted areas.
export function isMemberAllowedForRestrictedPostcode(member: string | null | undefined): boolean
{
    const normalized = String(member ?? '').trim()
    return (POSTCODE_ALLOWED_MEMBERS as readonly string[]).includes(normalized)
}

// Returns an error message when the postcode/member combination is not
// permitted, or null when it's fine to proceed.
export function restrictedPostcodeMessage(
    postcode: string | null | undefined,
    member: string | null | undefined,
): string | null
{
    if (!isRestrictedPostcode(postcode)) return null
    if (isMemberAllowedForRestrictedPostcode(member)) return null
    return RESTRICTED_POSTCODE_MESSAGE
}
